import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ShoppableOutfit from '@stitch-fix/mode-react/ShoppableOutfit';
import OutfitSandboxSideBySideComparison from './OutfitSandboxSideBySideComparison';
import useOutfitSandboxQuery from '../ProductDetailsPage/useOutfitSandboxQuery';
import { shopPdpPath } from '../../utils/urlUtils';
import { sampleOutfitIds } from './sampleOutfitIds';
import styles from './styles.module.scss';

const OutfitSandbox = ({ businessLine }) => {
  const [outfitId, setOutfitId] = useState('');
  const [skipOutfitSandboxQuery, setSkipOutfitSandboxQuery] = useState(true);

  const params = new URLSearchParams(window.location.search);

  const isAValidSkuId = skuId => {
    return Number(skuId) > 0 && typeof Number(skuId) === 'number';
  };

  const outfitIdIsValid = () => {
    const skuIds = outfitId.split('-');

    return skuIds.every(isAValidSkuId);
  };

  const [
    outfitGeneratedByCurrentAlgorithm,
    setOutfitGeneratedByCurrentAlgorithm,
  ] = useState(null);

  const [
    outfitGeneratedByExperimentalAlgorithm,
    setOutfitGeneratedByExperimentalAlgorithm,
  ] = useState(null);

  const [
    outfitCandidatesGeneratedByExperimentalAlgorithm,
    setOutfitCandidatesGeneratedByExperimentalAlgorithm,
  ] = useState(null);

  const { outfitSandboxData } = useOutfitSandboxQuery({
    outfitId,
    skip: skipOutfitSandboxQuery,
  });

  useEffect(() => {
    if (outfitSandboxData) {
      setOutfitGeneratedByCurrentAlgorithm(
        outfitSandboxData?.client?.outfitSandbox?.legacyOutfit || null,
      );
      setOutfitGeneratedByExperimentalAlgorithm(
        outfitSandboxData?.client?.outfitSandbox?.outfit || null,
      );
      setOutfitCandidatesGeneratedByExperimentalAlgorithm(
        outfitSandboxData?.client?.outfitSandbox?.allOutfitCandidates?.edges?.map(
          edge => {
            return edge.node;
          },
        ) || null,
      );
      setSkipOutfitSandboxQuery(true);
    }
  }, [outfitSandboxData]);

  const buildOutfitImageComponents = outfit =>
    outfit?.items?.map(item => {
      const sku = item?.shopProductVariant;

      if (!sku) {
        // Shop Product Catalog may not have imported the variant yet
        return null;
      }

      // TODO: primaryImage can technically be null here, which would cause an error.
      const primaryImage = sku?.image;
      const fallbackUrl = `${primaryImage?.baseUrl}${primaryImage?.mediaPath}`;
      const imageUrl = primaryImage?.medium || fallbackUrl;

      return {
        id: sku?.id,
        imageUrl,
        altText: primaryImage?.altText,
        labelText: item?.sticker?.label,
        laydown: item?.laydown,
        url: shopPdpPath(sku?.url, {
          source: 'outfit-sandbox',
          subSource: 'outfit-sandbox',
          outfitId: outfit.id,
          collection: 'outfit-sandbox',
          queryId: null,
        }),
      };
    }) || [];

  const showScoringInfo = params.get('showScoringInfo');

  return (
    <>
      <div className={styles.input_outer_container}>
        <div className={styles.input_inner_container}>
          <input
            className={styles.input}
            onChange={e => setOutfitId(e.target.value)}
            value={outfitId}
          />
          <button
            className={styles.input_button}
            type="button"
            onClick={() => setSkipOutfitSandboxQuery(!outfitIdIsValid())}
          >
            Laydown Outfit
          </button>
        </div>
      </div>
      <div className={styles.main_outfits_container}>
        <div styleName="box">
          <p className={styles.title}>Legacy Outfit Laydown</p>
          <ShoppableOutfit
            outfitId={outfitGeneratedByCurrentAlgorithm?.id || ''}
            imageComponents={buildOutfitImageComponents(
              outfitGeneratedByCurrentAlgorithm,
              item => !!item?.id,
            )}
            isLoading={false}
          />
          {showScoringInfo && (
            <>
              <p className={styles.outfit_info}>
                Total Laydown Score:{' '}
                {outfitGeneratedByCurrentAlgorithm?.laydownScoringBreakdown
                  ?.laydownScore || 'N/A'}
              </p>
              {outfitGeneratedByCurrentAlgorithm?.items.map(item => {
                return (
                  <div className={styles.outfit_item_info}>
                    <p className={styles.outfit_info}>
                      Laydown Item Type: {item.laydownItemType}
                    </p>
                    <p className={styles.outfit_info}>
                      Legacy Laydown Item Type: {item.legacyLaydownItemType}
                    </p>
                    <p className={styles.outfit_info}>
                      Total Laydown Score:{' '}
                      {item.laydownScoringBreakdown?.laydownScore}
                    </p>
                    <p className={styles.outfit_info}>
                      Weighting Factor:{' '}
                      {item.laydownScoringBreakdown?.laydownWeightingFactor}
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score:{' '}
                      {item.laydownScoringBreakdown?.laydownUnweightedScore}
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Occupying Whole Column:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForOccupyingWholeColumn
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Top:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsTop
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Bottom:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsBottom
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Vertical Center:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsVerticalCenter
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Left:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsLeft
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Right:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsRight
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Horizontal Center:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsHorizontalCenter
                      }
                    </p>
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div styleName="box">
          <p className={styles.title}>Experimental Outfit Laydown</p>
          <ShoppableOutfit
            outfitId={outfitGeneratedByExperimentalAlgorithm?.id || ''}
            imageComponents={buildOutfitImageComponents(
              outfitGeneratedByExperimentalAlgorithm,
              item => !!item?.id,
            )}
            isLoading={false}
          />
          {showScoringInfo && (
            <>
              <p className={styles.outfit_info}>
                Total Laydown Score:{' '}
                {outfitGeneratedByExperimentalAlgorithm?.laydownScoringBreakdown
                  ?.laydownScore || 'N/A'}
              </p>
              {outfitGeneratedByExperimentalAlgorithm?.items.map(item => {
                return (
                  <div className={styles.outfit_item_info}>
                    <p className={styles.outfit_info}>
                      Laydown Item Type: {item.laydownItemType}
                    </p>
                    <p className={styles.outfit_info}>
                      Legacy Laydown Item Type: {item.legacyLaydownItemType}
                    </p>
                    <p className={styles.outfit_info}>
                      Total Laydown Score:{' '}
                      {item.laydownScoringBreakdown?.laydownScore}
                    </p>
                    <p className={styles.outfit_info}>
                      Weighting Factor:{' '}
                      {item.laydownScoringBreakdown?.laydownWeightingFactor}
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score:{' '}
                      {item.laydownScoringBreakdown?.laydownUnweightedScore}
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Occupying Whole Column:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForOccupyingWholeColumn
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Top:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsTop
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Bottom:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsBottom
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Vertical Center:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsVerticalCenter
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Left:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsLeft
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Right:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsRight
                      }
                    </p>
                    <p className={styles.outfit_info}>
                      Unweighted Score For Being Towards Horizontal Center:{' '}
                      {
                        item.laydownScoringBreakdown
                          ?.laydownUnweightedScoreForBeingTowardsHorizontalCenter
                      }
                    </p>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
      {params.get('showExperimentalOutfitCandidates') && (
        <>
          <hr className={styles.sandbox_divider} />
          <div className={styles.all_outfit_candidates_outer_container}>
            <p className={styles.title}>All Experimental Outfit Candidates</p>
            <div className={styles.all_outfit_candidates_inner_container}>
              {outfitCandidatesGeneratedByExperimentalAlgorithm
                ?.sort((thisOutfitCandidate, thatOutfitCandidate) => {
                  return (
                    (thisOutfitCandidate.laydownScoringBreakdown
                      ?.laydownScore || 0) -
                    (thatOutfitCandidate.laydownScoringBreakdown
                      ?.laydownScore || 0)
                  );
                })
                .reverse()
                .map(outfitCandidate => {
                  return (
                    <div styleName="box">
                      <ShoppableOutfit
                        outfitId={outfitCandidate?.id || ''}
                        imageComponents={buildOutfitImageComponents(
                          outfitCandidate,
                          item => !!item?.id,
                        )}
                        isLoading={false}
                      />
                      {showScoringInfo && (
                        <>
                          <p className={styles.outfit_info}>
                            Total Laydown Score:{' '}
                            {
                              outfitCandidate.laydownScoringBreakdown
                                ?.laydownScore
                            }
                          </p>
                          {outfitCandidate.items.map(item => {
                            return (
                              <div className={styles.outfit_item_info}>
                                <p className={styles.outfit_info}>
                                  Laydown Item Type: {item.laydownItemType}
                                </p>
                                <p className={styles.outfit_info}>
                                  Legacy Laydown Item Type:{' '}
                                  {item.legacyLaydownItemType}
                                </p>
                                <p className={styles.outfit_info}>
                                  Total Laydown Score:{' '}
                                  {item.laydownScoringBreakdown?.laydownScore}
                                </p>
                                <p className={styles.outfit_info}>
                                  Weighting Factor:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownWeightingFactor
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScore
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Occupying Whole Column:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForOccupyingWholeColumn
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Being Towards Top:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForBeingTowardsTop
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Being Towards Bottom:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForBeingTowardsBottom
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Being Towards Vertical
                                  Center:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForBeingTowardsVerticalCenter
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Being Towards Left:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForBeingTowardsLeft
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Being Towards Right:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForBeingTowardsRight
                                  }
                                </p>
                                <p className={styles.outfit_info}>
                                  Unweighted Score For Being Towards Horizontal
                                  Center:{' '}
                                  {
                                    item.laydownScoringBreakdown
                                      ?.laydownUnweightedScoreForBeingTowardsHorizontalCenter
                                  }
                                </p>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
      {Object.entries(
        sampleOutfitIds[businessLine?.toLocaleLowerCase()] || {},
      ).map(outfitEntry => {
        return (
          <OutfitSandboxSideBySideComparison
            key={outfitEntry[1].id}
            outfitId={outfitEntry[1].id}
            title={outfitEntry[1].title}
          />
        );
      })}
    </>
  );
};

OutfitSandbox.propTypes = {
  businessLine: PropTypes.string.isRequired,
};

export default OutfitSandbox;
